import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot, doc, Timestamp,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import { Prestamo } from '../types';

/**
 * Datos del módulo Préstamos: objetos que dejas (o que te dejan) y tienen que
 * volver. Vive aparte de Finanzas porque no mueve dinero.
 */
interface PrestamosContextType {
  prestamos: Prestamo[];
  loadingPrestamos: boolean;

  crearPrestamo: (p: Omit<Prestamo, 'id' | 'usuarioId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  actualizarPrestamo: (id: string, datos: Partial<Prestamo>) => Promise<void>;
  /** Fecha en que volvió; null lo vuelve a dar por fuera. */
  marcarDevuelto: (id: string, fecha: Date | null) => Promise<void>;
  eliminarPrestamo: (id: string) => Promise<void>;
}

const PrestamosContext = createContext<PrestamosContextType | undefined>(undefined);

/**
 * Prepara los datos para Firestore: fechas a Timestamp y los opcionales vacíos
 * como null, porque Firestore no borra un campo por omitirlo en un update.
 */
const aFirestore = (d: Partial<Prestamo>) => {
  const fecha = (v: Date | undefined) => (v ? Timestamp.fromDate(new Date(v)) : null);
  const out: Record<string, any> = { ...d };
  if ('fechaPrestamo' in d) out.fechaPrestamo = fecha(d.fechaPrestamo);
  if ('fechaPrevista' in d) out.fechaPrevista = fecha(d.fechaPrevista);
  if ('fechaDevolucion' in d) out.fechaDevolucion = fecha(d.fechaDevolucion);
  if ('notas' in d) out.notas = d.notas || null;
  return out;
};

export const PrestamosProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Solo se escucha con el módulo activado: sin él, las reglas rechazan la
  // consulta y el listener muere; al activarlo, el efecto vuelve a suscribirse.
  const { tieneModulo } = usePerfil();
  const conModulo = tieneModulo('prestamos');
  const [prestamos, setPrestamos] = useState<Prestamo[]>([]);
  const [loadingPrestamos, setLoadingPrestamos] = useState(true);

  useEffect(() => {
    if (!user || !conModulo) {
      setPrestamos([]);
      setLoadingPrestamos(false);
      return;
    }

    setLoadingPrestamos(true);
    const q = query(collection(db, 'prestamos'), where('usuarioId', '==', user.uid));

    const unsub = onSnapshot(q, snap => {
      const lista = snap.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          direccion: data.direccion === 'recibido' ? 'recibido' : 'prestado',
          notas: data.notas ?? undefined,
          fechaPrestamo: data.fechaPrestamo?.toDate() || new Date(),
          fechaPrevista: data.fechaPrevista?.toDate() ?? undefined,
          fechaDevolucion: data.fechaDevolucion?.toDate() ?? undefined,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || data.createdAt?.toDate() || new Date(),
        };
      }) as Prestamo[];
      // Lo más reciente primero
      lista.sort((a, b) => b.fechaPrestamo.getTime() - a.fechaPrestamo.getTime());
      setPrestamos(lista);
      setLoadingPrestamos(false);
    }, err => {
      // Sin el módulo activado las reglas deniegan la lectura: no es un fallo
      console.warn('Préstamos no disponibles:', err.code);
      setPrestamos([]);
      setLoadingPrestamos(false);
    });

    return unsub;
  }, [user, conModulo]);

  const crearPrestamo: PrestamosContextType['crearPrestamo'] = async p => {
    if (!user) throw new Error('Sesión no iniciada');
    await setDoc(doc(collection(db, 'prestamos')), {
      ...aFirestore(p),
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  };

  const actualizarPrestamo = async (id: string, datos: Partial<Prestamo>) => {
    await updateDoc(doc(db, 'prestamos', id), { ...aFirestore(datos), updatedAt: Timestamp.now() });
  };

  const marcarDevuelto = async (id: string, fecha: Date | null) => {
    await actualizarPrestamo(id, { fechaDevolucion: fecha ?? undefined });
  };

  const eliminarPrestamo = async (id: string) => {
    await deleteDoc(doc(db, 'prestamos', id));
  };

  return (
    <PrestamosContext.Provider value={{
      prestamos, loadingPrestamos,
      crearPrestamo, actualizarPrestamo, marcarDevuelto, eliminarPrestamo,
    }}>
      {children}
    </PrestamosContext.Provider>
  );
};

export const usePrestamos = () => {
  const ctx = useContext(PrestamosContext);
  if (!ctx) throw new Error('usePrestamos debe usarse dentro de PrestamosProvider');
  return ctx;
};
